<template>
    <main class="search-page w-full m-auto py-6 px-2 md:py-12 md:px-6 min-h-screen">
        <client-only>
            <template v-if="searchIndex && meiliSearchClient">
                <!-- Kept alongside ais-instant-search (not just in #fallback) so the skeleton
                     stays up across the gap between client-only mounting and the first search
                     response resolving — ais-instant-search's own content stays invisible for
                     that whole gap (see hasLoadedOnce below), so without this the page would
                     otherwise go blank in between. -->
                <SearchSkeletonMeili v-if="!hasLoadedOnce" />

                <ais-instant-search
                    class="flex flex-col gap-6 group/search"
                    :index-name="searchIndex"
                    :search-client="meiliSearchClient"
                    :future="{ preserveSharedStateOnUnmount: true }"
                    :routing="routing"
                    :middlewares="middlewares"
                >
                    <ais-configure :hits-per-page.camel="hitsPerPage" />
                    <SearchQueryRedirectMeili @redirect-failed="redirectFailed = true" />

                    <ais-search-box class="relative md:hidden">
                        <template #default="{ isSearchStalled, refine }">
                            <span class="absolute inset-y-0 left-0 flex items-center pl-3 pb-px text-primary z-10">
                                <span v-if="isSearchStalled" class="loading" />
                                <MiscIcon v-else name="search" />
                            </span>
                            <input
                                type="text"
                                class="input block w-full md:min-w-[480px] px-10"
                                :value="displayValue"
                                :aria-label="$t('search.input.label')"
                                name="searchInput"
                                @input="onExecute(refine, ($event.currentTarget as HTMLInputElement).value)"
                            >
                            <button
                                v-if="displayValue"
                                type="button"
                                class="btn btn-sm btn-ghost btn-circle absolute inset-y-0 right-0 m-auto mr-1 pb-px flex justify-center items-center text-primary"
                                :aria-label="$t('search.input.closeSearch')"
                                @click="clearInput()"
                            >
                                <MiscIcon name="x" />
                            </button>
                        </template>
                    </ais-search-box>

                    <div class="flex flex-col md:flex-row md:gap-8">
                        <SearchSidebarMeili class="hidden md:w-56 md:shrink-0 md:group-has-[.ais-Hits]/search:block" />

                        <div class="flex-1 min-w-0 flex flex-col gap-6">
                            <div class="hidden group-has-[.ais-Hits]/search:block sticky top-0 z-20 -mr-2 -ml-2 -mt-2 pl-2 pr-2 pt-2 bg-base-100 md:relative md:ml-0 md:mr-0 md:mt-0 md:pt-0 md:pl-0 md:pr-0">
                                <div class="flex flex-col gap-3 w-full mb-4 md:gap-2 md:max-w-2xl md:min-h-[60px] md:mb-3">
                                    <div id="search-header-meili" />
                                </div>
                                <div class="flex gap-2 justify-between w-full border-b pb-4 md:border-b-0 md:pt-3 md:border-t">
                                    <ais-sort-by
                                        class="w-full md:absolute md:top-0 md:right-0 md:w-auto"
                                        :items="[
                                            { value: `${searchIndex}`, label: $t('search.sorter.featured') },
                                            { value: `${searchIndex}:price.net:asc`, label: $t('search.sorter.priceAsc') },
                                            { value: `${searchIndex}:price.net:desc`, label: $t('search.sorter.priceDesc') },
                                            { value: `${searchIndex}:name:asc`, label: $t('search.sorter.nameAsc') },
                                            { value: `${searchIndex}:name:desc`, label: $t('search.sorter.nameDesc') }
                                        ]"
                                    >
                                        <template #default="{ items, refine, currentRefinement, canRefine }">
                                            <div v-show="canRefine" class="form-control w-full md:w-auto relative">
                                                <label for="sorter" class="label sr-only">
                                                    <span class="label-text">{{ $t('search.sorter.sortBy') }}</span>
                                                </label>
                                                <span class="absolute inset-y-0 left-0 flex items-center pl-3 pb-px text-primary pointer-events-none z-10">
                                                    <MiscIcon name="arrow-up-down" />
                                                </span>
                                                <select
                                                    id="sorter"
                                                    class="select w-full md:w-auto pl-11"
                                                    @change="refine(($event.target as HTMLSelectElement).value)"
                                                >
                                                    <option
                                                        v-for="item in items"
                                                        :key="item.value"
                                                        :value="item.value"
                                                        :selected="item.value === currentRefinement"
                                                        v-text="item.label"
                                                    />
                                                </select>
                                            </div>
                                        </template>
                                    </ais-sort-by>
                                    <div class="w-full md:hidden">
                                        <button
                                            type="button"
                                            class="w-full btn btn-outline border-border"
                                            @click="sidenav.toggle('searchFilter')"
                                            @keydown.enter="sidenav.toggle('searchFilter')"
                                        >
                                            <MiscIcon name="settings-2" />
                                            {{ $t('search.input.filterButton') }}
                                        </button>
                                    </div>

                                    <Teleport defer to="#searchFilterTarget">
                                        <SearchFilterMeiliResultsBar />
                                        <SearchSidebarMeili mobile class="md:hidden" />
                                        <SearchFilterMeili />
                                    </Teleport>

                                    <SearchFilterMeili class="hidden md:block" />
                                </div>
                            </div>

                            <ais-state-results class="flex flex-col gap-4">
                                <!-- `results` is bound twice on purpose: whole for isRedirecting(), destructured for the rest. -->
                                <template #default="{ results, results: { hits, nbHits }, query }">
                                    <!-- Only mounted once ais-state-results has real results (see
                                         its own internal v-if) — the earliest reliable signal that
                                         the first search response has arrived. Stays rendered
                                         unconditionally: a slot that yields no vnode at all makes
                                         ais-state-results fall back to its documentation stub. -->
                                    <span
                                        :ref="el => markLoaded(el, results)"
                                        class="hidden"
                                        aria-hidden="true" />

                                    <template v-if="!isRedirecting(results)">
                                        <Teleport to="#search-header-meili">
                                            <div v-if="query" class="md:text-xl mb-1">
                                                <span class="font-semibold">{{ $t('search.input.resultsFor') }} „{{ query }}"</span> <span class="badge badge-secondary">{{ nbHits }}</span>
                                            </div>
                                        </Teleport>

                                        <div v-if="!hits.length">
                                            <p class="text-xl font-semibold leading-6 mb-4">
                                                {{ $t('search.input.noResultsHeadline', { query }) }}
                                            </p>
                                            <p class="text-sm font-semibold mb-2">
                                                {{ $t('search.input.noResultsSubHeadline') }}
                                            </p>
                                            <ul class="text-sm list-disc ml-3 pl-3 md:mb-6" style="list-style: disc;">
                                                <li>{{ $t('search.input.noResultsText1') }}</li>
                                                <li>{{ $t('search.input.noResultsText2') }}</li>
                                            </ul>
                                            <ais-search-box class="hidden md:flex md:justify-between md:items-center md:max-w-2xl md:gap-2">
                                                <template #default="{ currentRefinement, isSearchStalled, refine }">
                                                    <div class="relative w-full">
                                                        <span class="absolute inset-y-0 left-0 flex items-center pl-3 pb-px text-primary z-10">
                                                            <span v-if="isSearchStalled" class="loading" />
                                                            <MiscIcon v-else name="search" />
                                                        </span>
                                                        <input
                                                            v-model="noResultsSearchTerm"
                                                            type="text"
                                                            class="input block w-full md:min-w-[480px] px-10"
                                                            :aria-label="$t('search.input.label')"
                                                            @keydown.esc="noResultsSearchTerm = ''"
                                                            @keydown.enter="refine(noResultsSearchTerm)"
                                                        >
                                                        <button
                                                            v-if="currentRefinement"
                                                            type="button"
                                                            class="btn btn-sm btn-ghost btn-circle absolute inset-y-0 right-0 m-auto mr-1 pb-px flex justify-center items-center text-primary"
                                                            :aria-label="$t('search.input.closeSearch')"
                                                            @click="noResultsSearchTerm = ''"
                                                        >
                                                            <MiscIcon name="x" />
                                                        </button>
                                                    </div>
                                                    <button
                                                        class="btn btn-primary"
                                                        type="button"
                                                        :aria-label="$t('search.input.noResultsSubmitButton')"
                                                        @click="refine(noResultsSearchTerm)"
                                                    >
                                                        {{ $t('search.input.noResultsSubmitButton') }}
                                                    </button>
                                                </template>
                                            </ais-search-box>

                                            <EmarsysRecommenderWidget
                                                v-if="query"
                                                model="personal"
                                                container-id="emarsys-search-noresults"
                                                :limit="24"
                                                item-list-id="emarsys_noresults_recommendations"
                                                item-list-name="Emarsys No Results Recommendations"
                                                class="mt-6"
                                            />
                                        </div>

                                        <ais-hits v-if="hits.length">
                                            <template #default="{ items }">
                                                <ul class="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-6">
                                                    <ProductListingCard
                                                        v-for="(item, index) in mapSearchApiItems(items as unknown as SearchApiHit[], displayGross)"
                                                        :key="item.id"
                                                        :data="item"
                                                        @click="onListingCardClick(item, index)"
                                                    />
                                                </ul>
                                            </template>
                                        </ais-hits>

                                        <ais-pagination v-if="hits.length" class="m-auto md:mt-10">
                                            <template #default="{ currentRefinement, isFirstPage, refine }">
                                                <div v-show="deriveSearchPageCount(nbHits, hitsPerPage) > 1" class="flex">
                                                    <button
                                                        type="button"
                                                        :disabled="isFirstPage"
                                                        class="btn btn-ghost btn-square"
                                                        :aria-label="$t('search.pagination.first')"
                                                        @click.prevent="onClickFirst(refine)"
                                                    >
                                                        <MiscIcon name="chevrons-left" />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        :disabled="isFirstPage"
                                                        class="btn btn-ghost btn-square"
                                                        :aria-label="$t('search.pagination.prev')"
                                                        @click="onClickPrev(refine, currentRefinement)"
                                                    >
                                                        <MiscIcon name="chevron-left" />
                                                    </button>

                                                    <div class="flex items-center pb-px">
                                                        <span class="font-semibold text-sm text-center px-1">
                                                            {{ $t('search.pagination.pageOf', { page: currentRefinement + 1, max: deriveSearchPageCount(nbHits, hitsPerPage) }) }}
                                                        </span>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        class="btn btn-ghost btn-square"
                                                        :disabled="currentRefinement + 1 >= deriveSearchPageCount(nbHits, hitsPerPage) || hits.length < hitsPerPage"
                                                        :aria-label="$t('search.pagination.next')"
                                                        @click="onClickNext(refine, currentRefinement)"
                                                    >
                                                        <MiscIcon name="chevron-right" />
                                                    </button>
                                                </div>
                                            </template>
                                        </ais-pagination>
                                    </template>
                                </template>
                            </ais-state-results>
                        </div>
                    </div>
                </ais-instant-search>
            </template>

            <div v-else>
                No host or client id
            </div>

            <template #fallback>
                <SearchSkeletonMeili />
            </template>
        </client-only>
    </main>
</template>

<script setup lang="ts">
import { AisInstantSearch, AisSearchBox, AisConfigure, AisStateResults, AisHits, AisPagination, AisSortBy } from 'vue-instantsearch/vue3/es'
import { createInsightsMiddleware } from 'instantsearch.js/es/middlewares'
import qs from 'qs'
import type { LocationQuery } from 'vue-router'
import type { Schemas } from '#shopware'
import type { SearchApiHit } from '~/types/searchApi'
import { findSearchApiRedirect } from '~/utils/searchApiRedirect'
import { mapSearchApiItems } from '~/utils/mapSearchApiItems'
import { deriveSearchPageCount } from '~/utils/meiliResponseAdapter'

const nuxtApp = useNuxtApp()
const localePath = useLocalePath()
const { searchIndex, meiliSearchClient } = useMeiliSearch()
const vueRouter = useRouter()
const { sessionContext } = useSessionContext()
const sidenav = useSidenav()

const displayGross = computed(() => sessionContext.value?.currentCustomerGroup?.displayGross !== false)

const noResultsSearchTerm = ref('')

// The A4G Search API results page's own tracking identity in GTM/Emarsys payloads —
// named per the A4G Search API (not "meili" bare, per CONTEXT.md's domain glossary).
const TRACKING_LIST_ID = 'a4g_search_api_results'
const TRACKING_LIST_NAME = 'A4G Search API Results'

// insightsClient is deliberately null — this only observes the 'ais.hits'/'view' event
// instantsearch.js itself emits to re-dispatch it as the app's viewProductListing hook.
// It never sends real Insights events, and this page never sets
// click-analytics/data-insights-* attributes, so instantsearch.js's automatic Insights
// middleware never self-activates either.
const middlewares = ref()

// instantsearch.js's own view-event dedup (viewedObjectIDs) lives inside the branch that
// only runs when no custom onEvent is passed — supplying onEvent (required here, since
// insightsClient is null) bypasses it entirely, so 'view' fires again on every render
// (initial load, every refinement) even when the hits are unchanged. Dedupe here instead.
let lastViewSignature: string | undefined

// createSendEventForHits.js chunks the hits array into batches of 20 (the Insights
// per-event objectID cap) and calls onEvent once per chunk, synchronously, in the
// same tick — so a page of 24 hits fires onEvent twice (20, then the remaining 4), each
// with only its own chunk. Buffer chunks and flush on the next microtask, once the
// synchronous burst is done, so downstream hooks see the full, un-chunked hit set.
let pendingViewHits: SearchApiHit[] = []
let flushScheduled = false

function flushViewHits(): void {
    flushScheduled = false
    const hits = pendingViewHits
    pendingViewHits = []
    if (hits.length === 0) return

    const signature = hits.map(hit => hit.id).join('|')
    if (signature === lastViewSignature) return
    lastViewSignature = signature

    nuxtApp.callHook('viewProductListing', {
        id: TRACKING_LIST_ID,
        name: TRACKING_LIST_NAME,
        products: mapSearchApiItems(hits, displayGross.value)
    })
    // Built from the merged hits rather than forwarding the library's own `payload` —
    // that payload is per-chunk too (see above) and would carry only up to 20 objectIDs.
    nuxtApp.callHook('searchApiSearchView', {
        payload: {
            eventName: 'Hits Viewed',
            index: searchIndex.value ?? '',
            objectIDs: hits.map(hit => hit.id)
        }
    })
}

onMounted(() => {
    const trackingMiddleware = createInsightsMiddleware({
        insightsClient: null,
        onEvent(event) {
            const { widgetType, eventType, hits } = event

            if (widgetType !== 'ais.hits' || eventType !== 'view' || !hits || hits.length === 0) return

            pendingViewHits.push(...(hits as unknown as SearchApiHit[]))
            if (!flushScheduled) {
                flushScheduled = true
                queueMicrotask(flushViewHits)
            }
        }
    })

    middlewares.value = [trackingMiddleware]
})

function onListingCardClick(product: Schemas['Product'], index: number): void {
    const itemListContextStore = useItemListContextStore()

    itemListContextStore.setItemListContextData({
        item_list_id: TRACKING_LIST_ID,
        item_list_name: TRACKING_LIST_NAME,
        index
    })

    nuxtApp.callHook('clickProductListingCard', {
        id: TRACKING_LIST_ID,
        name: TRACKING_LIST_NAME,
        product,
        index
    })
}

// Flips once, when ais-state-results first mounts its slot (see markLoaded's usage in the
// template) — keeps the skeleton up across the gap between mounting and the first search
// response, instead of it disappearing before there's anything to replace it with.
const hasLoadedOnce = ref(false)
function markLoaded(el: unknown, results: unknown): void {
    if (el && !isRedirecting(results)) hasLoadedOnce.value = true
}

// Releases the hold below when the navigation did not happen — otherwise a redirect the
// router refuses would leave the page in its skeleton for good.
const redirectFailed = ref(false)

// A redirect rides along on the response that also carries the hits, so rendering it would
// paint the result page — usually as "no results" — for the length of the navigation. The
// results slot is gated on this instead, which keeps the skeleton up (markLoaded never runs)
// and suppresses the header teleport, the Emarsys recommender and the view event.
function isRedirecting(results: unknown): boolean {
    if (redirectFailed.value) return false

    return findSearchApiRedirect(results) !== null
}

let _removeAfterEach: (() => void) | undefined
// Set while `write()`'s own `router.push()` is in flight so the `afterEach` hook below
// can tell "InstantSearch just wrote this URL itself" apart from an external navigation
// (browser back/forward, or a fresh query pushed from the header search while already on
// this page). Without this guard, `write()`'s push also completes a navigation, `afterEach`
// fires for it too, and its `cb()` re-feeds the just-written state into InstantSearch as if
// it were external — triggering a second, identical `multi-search` request per refinement.
let isWritingRoute = false

// URL state sync
const routing = {
    router: {
        read(): LocationQuery {
            return vueRouter.currentRoute.value.query
        },
        write(routeState: LocationQuery): void {
            const currentFullPath = vueRouter.currentRoute.value.fullPath
            const newFullPath = vueRouter.resolve({
                path: vueRouter.currentRoute.value.path,
                query: routeState
            }).fullPath

            if (decodeURIComponent(currentFullPath) !== decodeURIComponent(newFullPath)) {
                isWritingRoute = true
                vueRouter.push({ query: routeState }).finally(() => {
                    isWritingRoute = false
                })
            }
        },
        createURL(routeState: LocationQuery): string {
            return vueRouter.resolve({ query: routeState }).href
        },
        onUpdate(cb: (route: LocationQuery) => void): void {
            if (typeof window === 'undefined') return

            // `afterEach` fires for every completed navigation — self-written, browser
            // back/forward, and any other in-app push (e.g. the header search box
            // navigating to `/search?...` while this page is already mounted) — so it
            // alone covers everything `onUpdate` needs; a separate `popstate` listener
            // would just fire `cb()` a second time for the same back/forward navigation.
            _removeAfterEach = vueRouter.afterEach(() => {
                if (isWritingRoute) return
                if (vueRouter.currentRoute.value.path.includes('search')) {
                    cb(vueRouter.currentRoute.value.query)
                }
            })
        },
        dispose(): void {
            if (typeof window === 'undefined') return

            if (_removeAfterEach) {
                _removeAfterEach()
            }
        }
    },
    stateMapping: {
        stateToRoute(uiState: object): LocationQuery {
            const filteredState = Object.fromEntries(
                Object.entries(uiState).map(([indexId, indexState]) => {
                    const { configure: _configure, ...rest } = indexState as Record<string, unknown>
                    return [indexId, rest]
                })
            )
            // `encode: true` (qs's default) is required here — a refinement value can
            // itself contain `&` or `=` (e.g. the category "Zubehör & Services"), and only
            // encoded values make a plain `split('&')` below unambiguous. The split pairs
            // are then decoded back to the plain strings `write()`/vue-router expect in a
            // `LocationQuery` (vue-router does its own encoding when building the href).
            const rawStr = qs.stringify(filteredState)
            if (!rawStr) return {}
            return rawStr.split('&').reduce((acc, pair) => {
                const eqIdx = pair.indexOf('=')
                if (eqIdx === -1) return acc
                acc[decodeURIComponent(pair.slice(0, eqIdx))] = decodeURIComponent(pair.slice(eqIdx + 1))
                return acc
            }, {} as Record<string, string>) as LocationQuery
        },
        routeToState(routeState: LocationQuery): object {
            // `routeState` values are already decoded by vue-router, so a value containing a
            // literal `&` or `=` must be re-encoded before joining — otherwise it gets
            // mistaken for a pair separator and silently truncated (see `stateToRoute` above).
            const parts: string[] = []
            for (const [key, value] of Object.entries(routeState)) {
                if (value === null || value === undefined) continue
                const vals = Array.isArray(value) ? value : [value]
                for (const v of vals) {
                    if (v !== null) parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`)
                }
            }
            return qs.parse(parts.join('&'))
        }
    }
}

const hitsPerPage = ref(24)
const route = useRoute()

const hasNonEmptyQuery = Object.entries(route.query).some(
    ([key, value]) => key.endsWith('[query]') && typeof value === 'string' && value.trim() !== ''
)
if (!hasNonEmptyQuery) {
    await navigateTo(localePath('/'))
}

// Pagination click handlers — scroll to top
function scrollToTopAndRefine(refine: (page: number) => void, page: number): void {
    window.scrollTo({ top: 0 })
    refine(page)
}

function onClickFirst(refine: (page: number) => void): void {
    scrollToTopAndRefine(refine, 0)
}

function onClickPrev(refine: (page: number) => void, currentPage: number): void {
    scrollToTopAndRefine(refine, currentPage - 1)
}

function onClickNext(refine: (page: number) => void, currentPage: number): void {
    scrollToTopAndRefine(refine, currentPage + 1)
}

// This bar is mobile-only; on desktop the header autocomplete sits in its place, so it
// runs through the same `useSearchSuggest` gate. Its X used to `refine('')`, which — this
// instance being URL-bound (see `routing`) — emptied the `[query]` parameter and left the
// page announcing that a search for nothing found nothing. It now only clears the field,
// matching the header's X on desktop: the results stand until a new term is searched, and
// likewise while the field holds a term too short to search.
const queryFromRoute = computed(() => {
    const entry = Object.entries(route.query).find(([key]) => key.endsWith('[query]'))
    return typeof entry?.[1] === 'string' ? entry[1] : ''
})

const displayValue = ref(queryFromRoute.value)
const lastRefinedTerm = ref(queryFromRoute.value)

// Adopt the term of navigations this component did not cause itself (back/forward, a query
// pushed from the header) — but never of its own `refine()`, whose URL write can land
// after the next keystroke and would then overwrite what is being typed.
watch(queryFromRoute, (term) => {
    if (term !== lastRefinedTerm.value) displayValue.value = term
})

const { handleInput, cancelPending } = useSearchSuggest({
    onRefine: (term) => { lastRefinedTerm.value = term },
})

const onExecute = (refine: (value: string) => void, term: string): void => {
    displayValue.value = term
    handleInput(refine, term)
}

const clearInput = (): void => {
    cancelPending()
    displayValue.value = ''
}
</script>
